import { Modal, Pressable, ScrollView, Text, View } from 'react-native'
import { useState } from 'react'
import type { ReactNode } from 'react'

interface AdminModalProps {
  visible: boolean
  onClose: () => void
  closeAccessibilityLabel: string
  title: string
  subtitle?: string
  icon?: ReactNode
  children: ReactNode
  actions?: ReactNode
}

export function AdminModal({
  visible,
  onClose,
  closeAccessibilityLabel,
  title,
  subtitle,
  icon,
  children,
  actions,
}: AdminModalProps) {
  const isCentered = !!icon
  const [scrolled, setScrolled] = useState(false)
  const [hasMoreBelow, setHasMoreBelow] = useState(false)
  const [scrollViewHeight, setScrollViewHeight] = useState(0)

  function handleScroll(e: { nativeEvent: { contentOffset: { y: number }; contentSize: { height: number }; layoutMeasurement: { height: number } } }) {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent
    setScrolled(contentOffset.y > 4)
    setHasMoreBelow(contentOffset.y + layoutMeasurement.height < contentSize.height - 4)
  }

  function handleContentSizeChange(_: number, contentHeight: number) {
    setHasMoreBelow(contentHeight > scrollViewHeight)
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View className="flex-1">
        <Pressable
          className="absolute inset-0 bg-black/50"
          onPress={onClose}
          accessibilityLabel={closeAccessibilityLabel}
        />
        <View className="flex-1 items-center justify-center px-4 py-6" pointerEvents="box-none">
          <View
            className="w-full max-w-[430px] overflow-hidden rounded-xl bg-canvas-card"
            style={{ maxHeight: '90%' }}
            onStartShouldSetResponder={() => true}
          >
            <View className="flex-1">
              <ScrollView
                bounces={false}
                showsVerticalScrollIndicator={true}
                contentContainerStyle={{ padding: 24 }}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                onContentSizeChange={handleContentSizeChange}
                onLayout={(e) => setScrollViewHeight(e.nativeEvent.layout.height)}
              >
                {icon ? <View className="mb-4 items-center">{icon}</View> : null}
                <Text className={`text-xl font-bold text-primary ${isCentered ? 'text-center' : ''}`}>
                  {title}
                </Text>
                {subtitle ? (
                  <Text className={`mt-1 text-sm font-normal text-secondary ${isCentered ? 'text-center' : ''}`}>
                    {subtitle}
                  </Text>
                ) : null}
                {children}
              </ScrollView>

              {scrolled && (
                <View
                  pointerEvents="none"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 32,
                    backgroundImage: 'linear-gradient(to bottom, #FFFFFF, transparent)',
                  } as object}
                />
              )}

              {hasMoreBelow && (
                <View
                  pointerEvents="none"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 32,
                    backgroundImage: 'linear-gradient(to top, #FFFFFF, transparent)',
                  } as object}
                />
              )}
            </View>

            {actions ? (
              <View className="px-6 pb-6 pt-4">
                {actions}
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  )
}